import CircularProgress from '@material-ui/core/CircularProgress';
import { green, red, yellow } from '@material-ui/core/colors';
import IconButton from '@material-ui/core/IconButton';
import LockIcon from '@material-ui/icons/Lock';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import React, { MouseEventHandler, useEffect, useState } from 'react';
import UnlockDialog from './UnlockDialog';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import { Box } from 'home/useBoxes';
import { BoxStatus } from 'home/useSubBoxStatuses';
import { getClientSdk } from 'graphql/client';
import { GetUserToken, useAuth } from 'components/AuthProvider';
import {
  UnlockBoxMutation,
  UnlockBoxMutationVariables,
  Unlock_Request_Type_Enum,
} from 'generated/graphql';
import { useMutation, queryCache } from 'react-query';
import { GraphQLError } from 'graphql-request/dist/types';
import Tooltip from '@material-ui/core/Tooltip';
import { Label } from 'labels';

type OpenBoxParams = {
  getUserToken: GetUserToken;
  variables: UnlockBoxMutationVariables;
};

const openBox = async ({ getUserToken, variables }: OpenBoxParams) => {
  const token = await getUserToken();
  return await getClientSdk(token).UnlockBox(variables);
};

const useOpenBox = () => {
  const { getUserToken } = useAuth();

  const mutateOpenBox = useMutation<
    UnlockBoxMutation,
    GraphQLError,
    UnlockBoxMutationVariables
  >((variables) => openBox({ getUserToken, variables }), {
    onMutate: (data) => {
      queryCache.setQueryData<BoxStatus | undefined>(
        ['status', data.boxId],
        (old) => {
          if (!old) return undefined;
          return { ...old, requestStatus: Unlock_Request_Type_Enum.Request };
        }
      );
    },
  });

  return mutateOpenBox;
};

interface Props {
  box: Box;
  status: BoxStatus;
}

const PROGRESS_SIZE = 40;

const useStyles = makeStyles((theme: Theme) => ({
  lockBtn: {
    color: ({ status }: Props) =>
      !status.isOnline
        ? theme.palette.grey[500]
        : status.isUnlocked
        ? red[500]
        : green[500],
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -PROGRESS_SIZE / 2, // progress size 40/2
    marginLeft: -PROGRESS_SIZE / 2, // progress size 40/2
    color: yellow[700],
  },
}));

const UnlockButton = ({ status, box }: Props) => {
  const classes = useStyles({ status, box });
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [openBox] = useOpenBox();

  const onLockBtnClick: MouseEventHandler = () => {
    setIsDialogOpen(true);
  };

  useEffect(() => {
    if (!status.isOnline) setIsDialogOpen(false);
  }, [status.isOnline]);

  const onDialogClose = () => setIsDialogOpen(false);

  const onOk = () => {
    onDialogClose();
    openBox({ boxId: box.id, imei: box.imei });
  };

  const isShowProgress =
    status.isOnline &&
    status.requestStatus === Unlock_Request_Type_Enum.Request;

  return (
    <>
      <Tooltip title={status.isUnlocked ? Label.KeyOpen : Label.KeyClose}>
        <span>
          <IconButton
            onClick={onLockBtnClick}
            aria-label="lock"
            className={classes.lockBtn}
            disabled={!status.isOnline}
          >
            {isShowProgress ? (
              <CircularProgress
                className={classes.buttonProgress}
                size={PROGRESS_SIZE}
              />
            ) : null}
            {status.isUnlocked ? <LockOpenIcon /> : <LockIcon />}
          </IconButton>
        </span>
      </Tooltip>

      <UnlockDialog
        status={status}
        box={box}
        onDialogClose={onDialogClose}
        onOk={onOk}
        isDialogOpen={isDialogOpen}
        isLoading={status.requestStatus === Unlock_Request_Type_Enum.Request}
      />
    </>
  );
};

export default UnlockButton;
